import React, { useEffect, useRef, useState } from 'react';
import { RewardBurst } from './RewardBurst';
import { threshold } from './engine';

export function FameMeter({ xp, tier, reducedMotion, draftsPending = 0 }: { xp: number; tier: number; reducedMotion: boolean; draftsPending?: number }) {
    const current = useRef({ xp, tier, holdUntil: 0 });
    const previous = useRef(xp);
    const [view, setView] = useState({ xp, tier });
    const [gain, setGain] = useState(0);
    const [levelUp, setLevelUp] = useState(0);
    const clearGain = useRef<number | undefined>(undefined);
    useEffect(() => {
        const delta = xp - previous.current;
        previous.current = xp;
        window.clearTimeout(clearGain.current);
        if (delta > 0) setGain(g => g + delta);
        const settle = () => { clearGain.current = window.setTimeout(() => setGain(0), 1600); };
        if (reducedMotion || xp < current.current.xp) {
            if (tier > current.current.tier) setLevelUp(tier);
            current.current = { xp, tier, holdUntil: 0 };
            setView({ xp, tier });
            settle();
            return;
        }
        let frame: number;
        let last = performance.now();
        const rate = Math.max(.001, (xp - current.current.xp) / 900);
        const animate = (now: number) => {
            const dt = Math.min(50, now - last); last = now;
            const c = current.current;
            if (c.tier < tier && c.xp >= threshold(c.tier + 1)) {
                if (!c.holdUntil) c.holdUntil = now + 180;
                if (now >= c.holdUntil) {
                    c.tier++; c.holdUntil = 0;
                    setLevelUp(c.tier);
                }
            } else if (c.xp < xp) {
                const boundary = c.tier < 20 ? threshold(c.tier + 1) : xp;
                c.xp = Math.min(xp, boundary, c.xp + rate * dt);
            }
            setView({ xp: c.xp, tier: c.tier });
            if (c.xp < xp || c.tier < tier) frame = requestAnimationFrame(animate);
            else settle();
        };
        frame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frame);
    }, [xp, tier, reducedMotion]);
    useEffect(() => {
        if (!levelUp) return;
        const timer = window.setTimeout(() => setLevelUp(0), 1800);
        return () => window.clearTimeout(timer);
    }, [levelUp]);
    useEffect(() => () => window.clearTimeout(clearGain.current), []);
    const span = view.tier < 20 ? threshold(view.tier + 1) - threshold(view.tier) : 1;
    const percent = view.tier === 20 ? 100 : Math.max(0, Math.min(100, (view.xp - threshold(view.tier)) / span * 100));
    return <div className={`fame${levelUp ? ' fame-level-up' : ''}`}>
        {levelUp > 0 && <div className="fame-reward"><RewardBurst key={levelUp} label={`FAME ${levelUp} ↑`} detail={draftsPending > 0 ? 'CARD DRAFT EARNED' : 'LEVEL UP'} cards={draftsPending > 0}/></div>}
        <span>FAME <strong key={view.tier}>{view.tier}</strong><small>/ 20</small></span>
        <div className="fame-bar-row"><div className="meter" role="progressbar" aria-label="Fame XP" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(percent)}><i style={{ width: `${percent}%` }}/></div>
        <span className="fame-raw-xp">{view.tier === 20 ? 'The company is legendary' : `${Math.round(view.xp - threshold(view.tier))} / ${span} EXP`}</span></div>
        <div className="fame-gain" role="status">{gain > 0 && <span>+{Math.round(gain)} XP</span>}{levelUp > 0 && <b key={levelUp}>LEVEL UP</b>}</div>
    </div>;
}
